import { Box, Button } from "@mui/material";
import { alpha } from "@mui/material/styles";
import { Check, LockOutlined, PersonAddAlt1 } from "@mui/icons-material";
import { IAtribuicaoChat } from "../../../contexts/atendimento/WebSocketAtendimentoContext";

interface BarraAtribuicaoChatProps {
    atribuicao?: IAtribuicaoChat;
    operadorUuidAtual: string | null;
    onAssumir: () => void;
    onLiberar: () => void;
}

/**
 * Controle de atribuição inline no header da conversa (quando há 2+ operadores).
 * Visual em "pílula": badge de status + botão de ação (Assumir/Liberar).
 * - Chat sem dono: badge "DISPONÍVEL" + Assumir.
 * - Chat seu: badge verde "ATENDENDO" + Liberar.
 * - Chat de outro: badge âmbar "{nome}" + Assumir (reassume/rouba).
 */
export const BarraAtribuicaoChat: React.FC<BarraAtribuicaoChatProps> = ({
    atribuicao,
    operadorUuidAtual,
    onAssumir,
    onLiberar,
}) => {
    const meu = !!atribuicao && atribuicao.operadorUuid === operadorUuidAtual;
    const deOutro = !!atribuicao && !meu;

    const badge = meu
        ? { paleta: 'success.main', icone: <Check sx={{ fontSize: 13 }} />, texto: 'ATENDENDO' }
        : deOutro
            ? { paleta: 'warning.main', icone: <PersonAddAlt1 sx={{ fontSize: 13 }} />, texto: atribuicao?.nome ?? 'OPERADOR' }
            : { paleta: 'text.secondary', icone: null, texto: 'DISPONÍVEL' };

    const acao = meu
        ? { rotulo: 'Liberar', icone: <LockOutlined />, onClick: onLiberar }
        : { rotulo: 'Assumir', icone: <PersonAddAlt1 />, onClick: onAssumir };

    return (
        <Box
            sx={{
                display: 'flex',
                alignItems: 'stretch',
                gap: 0.5,
                p: 0.5,
                borderRadius: 999,
                bgcolor: 'background.paper',
                border: (theme) => `1px solid ${theme.palette.divider}`,
                boxShadow: 1,
            }}
        >
            <Box
                sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 0.5,
                    px: 1.5,
                    py: 0.75,
                    borderRadius: 999,
                    fontSize: 10,
                    fontWeight: 700,
                    letterSpacing: '0.06em',
                    textTransform: 'uppercase',
                    lineHeight: 1,
                    maxWidth: 160,
                    color: badge.paleta,
                    bgcolor: (theme) => {
                        const cor = badge.paleta === 'text.secondary' ? theme.palette.text.primary : (
                            badge.paleta === 'success.main' ? theme.palette.success.main : theme.palette.warning.main
                        );
                        return alpha(cor, 0.12);
                    },
                    '& svg': { flexShrink: 0 },
                }}
            >
                {badge.icone}
                <Box component="span" sx={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {badge.texto}
                </Box>
            </Box>

            <Button
                size="small"
                onClick={acao.onClick}
                startIcon={acao.icone}
                sx={{
                    minWidth: 0,
                    px: 1.5,
                    py: 0.5,
                    fontSize: 10,
                    fontWeight: 700,
                    letterSpacing: '0.06em',
                    textTransform: 'uppercase',
                    color: 'text.secondary',
                    borderRadius: 999,
                    '& .MuiButton-startIcon': { mr: 0.5 },
                    '& svg': { fontSize: 15 },
                    '&:hover': { color: 'primary.main', bgcolor: 'transparent' },
                }}
            >
                {acao.rotulo}
            </Button>
        </Box>
    );
};
